import React, { useEffect, useMemo, useState } from "react";
import { Box, Typography, Button } from "@mui/material";
import { decryptTheParams } from "../../Helper/QueryParams/EncryptDecrypt";
import { getPagesMetaData } from "../../Api/Api";
import { getServiceRecordDetail } from "../../Api/ServiceRecordApi";
import CartLoader from "../../Helper/Loaders/CartLoader";
import ScreenContainer from "../../Components/ScreenContainer";
import RecordReplacementReview from "./review/RecordReplacementReview";
import TextWithCopy from "../../Features/Table/GenericDraggableTable/Renderers/columnTypes/TextWithCopy";
import {
    mapRecordToReviewState,
    formatRequestID,
} from "../DigitalTwinV3/Tabs/Components/ReplacementLogs/Data/replacementLogsHelper";

// Read-only full-page view of one replacement log, opened in a new tab from the
// Replacement Logs table (§C: reuses the same review layout the create flow uses,
// with per-document download and no immutability warning).
export default function ReplacementLogDetailPage() {
    const params = useMemo(() => decryptTheParams(), []);
    const { batteryID, serviceRecordID, vehicleNumber, pageType } = params || {};
    const metaPageName = pageType === "IOTDigitalTwin" ? "IOTDigitalTwin" : "digitalTwin";

    const [loading, setLoading] = useState(true);
    const [config, setConfig] = useState({});
    const [view, setView] = useState(null);

    useEffect(() => {
        let active = true;
        (async () => {
            setLoading(true);
            try {
                const [metaRes, recRes] = await Promise.all([
                    getPagesMetaData(metaPageName),
                    getServiceRecordDetail(serviceRecordID),
                ]);
                const cfg =
                    metaRes?.response?.response?.metaInfo?.components?.replacement || {};
                const record = recRes?.response?.data ?? recRes?.response ?? null;
                if (!active) return;
                setConfig(cfg);
                setView(record ? mapRecordToReviewState(record, cfg) : null);
            } catch (err) {
                console.error("Failed to load replacement log:", err);
                if (active) setView(null);
            } finally {
                if (active) setLoading(false);
            }
        })();
        return () => {
            active = false;
        };
    }, [serviceRecordID, metaPageName]);

    // Opened in its own tab; "Back" closes it.
    const handleBack = () => window.close();

    const packCount = view?.formState?.packs?.length || 0;

    return (
        <ScreenContainer pagesContent={null}>
            <Box sx={{ px: "24px", pt: "14px", pb: "80px" }}>
                {/* Header: breadcrumb + title + back */}
                <Box
                    sx={{
                        position: "sticky",
                        top: "50px",
                        zIndex: (theme) => theme.zIndex.appBar + 1,
                        backgroundColor: "#fff",
                        borderBottom: "1px solid #EAEAEA",
                        mx: "-24px",
                        px: "24px",
                        py: "16px",
                        mb: "4px",
                    }}
                >
                    <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
                        <Box>
                            <Box sx={{ display: "flex", alignItems: "center", gap: "8px", mb: "6px" }}>
                                <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#8D8D8D" }}>
                                    FLEET
                                </Typography>
                                <Typography sx={{ fontSize: "13px", color: "#8D8D8D" }}>/</Typography>
                                <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#4A4AF4" }}>
                                    {vehicleNumber || batteryID}
                                </Typography>
                                {/* A request can cover several packs, so the chip counts
                                    them rather than naming one slot. */}
                                {packCount > 0 && (
                                    <Box
                                        data-testid="detail-pack-count"
                                        sx={{
                                            fontSize: "12px",
                                            color: "#494949",
                                            backgroundColor: "#F0F0F0",
                                            borderRadius: "4px",
                                            padding: "2px 10px",
                                        }}
                                    >
                                        {packCount === 1
                                            ? [
                                                  view.formState.packs[0].stringLabel,
                                                  view.formState.packs[0].packLabel,
                                              ]
                                                  .filter(Boolean)
                                                  .join(" ")
                                            : `${packCount} packs`}
                                    </Box>
                                )}
                            </Box>
                            <Box sx={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
                                <Typography sx={{ fontSize: "26px", fontWeight: 700, color: "#1A1A1A" }}>
                                    Replacement log
                                </Typography>
                                {/* The request id identifies this whole request. Shown
                                    truncated with a copy button, since a uuid4 is not
                                    something anyone retypes. */}
                                {view?.requestID && (
                                    <Box
                                        data-testid="detail-request-id"
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "4px",
                                            backgroundColor: "#EEF0FF",
                                            borderRadius: "6px",
                                            padding: "3px 6px 3px 10px",
                                        }}
                                    >
                                        <Typography sx={{ fontSize: "12px", color: "#6B6B6B" }}>
                                            Request ID
                                        </Typography>
                                        <TextWithCopy
                                            text={view.requestID}
                                            label={formatRequestID(view.requestID)}
                                            type="Request ID"
                                        />
                                    </Box>
                                )}
                            </Box>
                        </Box>

                        <Button
                            data-testid="replacement-log-back"
                            onClick={handleBack}
                            sx={{
                                textTransform: "none",
                                fontSize: "14px",
                                fontWeight: 500,
                                color: "#494949",
                                border: "1px solid #D0D0D0",
                                borderRadius: "8px",
                                padding: "8px 22px",
                                "&:hover": { backgroundColor: "#F5F5F5" },
                            }}
                        >
                            Back to replacement logs
                        </Button>
                    </Box>
                </Box>

                {/* Sub-breadcrumb */}
                <Box sx={{ display: "flex", alignItems: "center", gap: "8px", py: "16px" }}>
                    <Typography sx={{ fontSize: "15px", color: "#8D8D8D" }}>
                        Digital Twin/Component/{" "}
                        <span style={{ color: "#1A1A1A", fontWeight: 600 }}>Record Replacement</span>
                    </Typography>
                </Box>

                {loading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "50vh" }}>
                        <CartLoader />
                    </Box>
                ) : view ? (
                    <RecordReplacementReview
                        formState={view.formState}
                        config={config}
                        preview={view.preview}
                        showWarning={false}
                        serviceRecordID={serviceRecordID}
                        timelineIsUtc={true}
                    />
                ) : (
                    <Typography sx={{ textAlign: "center", color: "#8D8D8D", mt: "40px" }}>
                        Replacement log not found.
                    </Typography>
                )}
            </Box>
        </ScreenContainer>
    );
}
